import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Container
} from '@mui/material';
import { CheckCircle, Cancel, Info } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/api';
import Layout from '../../components/Layout';

const PendingUsers = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve' or 'reject'
  const [userToAction, setUserToAction] = useState(null);

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPendingUsers();
      setPendingUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch pending users');
      // console.error('Error fetching pending users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedUser(null);
  };

  const handleOpenConfirmDialog = (action, user) => {
    setConfirmAction(action);
    setUserToAction(user);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
    setUserToAction(null);
  };

  const handleConfirmAction = async () => {
    if (confirmAction === 'approve') {
      await handleApprove(userToAction.userId);
    } else if (confirmAction === 'reject') {
      await handleReject(userToAction.userId);
    }
    handleCloseConfirmDialog();
  };

  const handleApprove = async (userId) => {
    try {
      setActionLoading(true);
      await adminAPI.approveUser(userId);
      toast.success('User approved successfully!');
      fetchPendingUsers();
      handleCloseDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve user');
      // console.error('Error approving user:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (userId) => {
    try {
      setActionLoading(true);
      await adminAPI.rejectUser(userId);
      toast.success('User registration rejected');
      fetchPendingUsers();
      handleCloseDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject user');
      // console.error('Error rejecting user:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Pending User Registrations
          </Typography>
          <Chip 
            label={`${pendingUsers.length} Pending`} 
            color="warning" 
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {pendingUsers.length === 0 ? (
          <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              No pending registrations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              All user registrations have been processed
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 600, sm: 750 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Registered On</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.companyName || '-'}</TableCell>
                    <TableCell>
                      <Chip label={user.category} size="small" />
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Info />}
                          onClick={() => handleViewDetails(user)}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Details
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => handleOpenConfirmDialog('approve', user)}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => handleOpenConfirmDialog('reject', user)}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* User Details Dialog */}
        <Dialog open={detailsDialogOpen} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
            Registration Details
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {selectedUser && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    First Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.firstName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Last Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.email}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Company Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.companyName || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.phoneNumber || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.category}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    State
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.states || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Membership Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.membershipNumber || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Registered On
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(selectedUser.createdAt)}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={handleCloseDetails} variant="outlined">
              Close
            </Button>
            <Button
              onClick={() => handleOpenConfirmDialog('reject', selectedUser)}
              variant="contained"
              color="error"
              startIcon={<Cancel />}
              disabled={actionLoading}
            >
              Reject
            </Button>
            <Button
              onClick={() => handleOpenConfirmDialog('approve', selectedUser)}
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              disabled={actionLoading}
            >
              Approve
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>
            {confirmAction === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {confirmAction === 'approve' 
                ? `Are you sure you want to approve the registration for ${userToAction?.firstName} ${userToAction?.lastName}?`
                : `Are you sure you want to reject the registration for ${userToAction?.firstName} ${userToAction?.lastName}?`
              }
            </Typography>
            {userToAction && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Email: {userToAction.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Company: {userToAction.companyName || '-'}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseConfirmDialog} variant="outlined" disabled={actionLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              variant="contained"
              color={confirmAction === 'approve' ? 'success' : 'error'}
              disabled={actionLoading}
              startIcon={confirmAction === 'approve' ? <CheckCircle /> : <Cancel />}
            >
              {actionLoading ? 'Processing...' : (confirmAction === 'approve' ? 'Approve' : 'Reject')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default PendingUsers;
